<template>
  <v-chart class="chart" :option="option" autoresize />
</template>

<script setup lang="ts">
import {computed, ComputedRef} from 'vue';
import type {ComposeOption} from 'echarts';
import {use} from 'echarts/core';
import {PieChart, PieSeriesOption} from 'echarts/charts';
import {CanvasRenderer} from 'echarts/renderers';
import VChart from 'vue-echarts';

import {
  GridComponent,
  LegendComponent,
  LegendComponentOption,
  TitleComponent,
  TitleComponentOption,
  TooltipComponent,
  TooltipComponentOption,
} from 'echarts/components';
import StudentMistakeLabelClustersResultsCluster = App.DTOs.Analytics.StudentMistakeLabelClustersResultsCluster;
import StudentMistakeLabelClustersResults = App.DTOs.Analytics.StudentMistakeLabelClustersResults; // <-- Update this import path as needed

use([TitleComponent, LegendComponent, TooltipComponent, GridComponent, PieChart, CanvasRenderer]);

type ECOption = ComposeOption<
  PieSeriesOption | TitleComponentOption | TooltipComponentOption | LegendComponentOption
>;

const props = defineProps<{
  clusterData: StudentMistakeLabelClustersResults;
}>();

const colorPalette = [
  '#ff5733',
  '#33b5e5',
  '#ffcc33',
  '#2ecc71',
  '#9b59b6',
  '#e74c3c',
  '#f39c12',
  '#3498db',
  '#1abc9c',
  '#8e44ad',
];

// Pie data from clusters
const pieData = computed(() =>
  props.clusterData.clusters.map(
    (cluster: StudentMistakeLabelClustersResultsCluster, index: number) => ({
      name: cluster.name ?? `Cluster ${index + 1}`,
      value: cluster.counts,
    })
  )
);

const option: ComputedRef<ECOption> = computed(() => ({
  tooltip: {
    trigger: 'item',
    formatter: (params: any) => {
      return `${params.name}: ${params.percent}%`;
    },
  },
  legend: {
    orient: 'vertical',
    top: '2%',
    right: '2%',
    itemGap: 8,
    data: props.clusterData.clusters.map(
      (cluster: StudentMistakeLabelClustersResultsCluster, index: number) => ({
        name: cluster.name ?? `Cluster ${index + 1}`,
        icon: 'circle',
      })
    ),
    textStyle: {
      color: '#333',
      fontSize: 14,
    },
  },
  series: [
    {
      name: 'Mistake Labels',
      type: 'pie',
      radius: ['30%', '60%'],
      center: ['40%', '50%'],
      avoidLabelOverlap: true,
      data: pieData.value.map((item: any, index: number) => ({
        ...item,
        itemStyle: {
          color: colorPalette[index % colorPalette.length],
        },
      })),
      itemStyle: {
        borderRadius: 10,
        borderColor: '#fff',
        borderWidth: 2,
      },
      label: {
        fontSize: 14,
      },
    },
  ],
}));
</script>

<style scoped>
.chart {
  width: 100%;
  aspect-ratio: 2;
  max-height: 100vh;
}
</style>
